///<reference path='../shared/xlsx.d.ts'/>

import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { RowNode, GridOptions, CsvExportParams, ProcessCellForExportParams } from 'ag-grid-community';
import { CovanceGridColumn } from './covance-grid-column';
import { isEmpty, some } from 'lodash';
import { MdlDialogService } from '@angular-mdl/core';
import * as xlsx from 'xlsx';
import { first } from 'rxjs/operators';

interface ExportType {
    title: string;
    exporter: () => void;
}

export interface SortModel {
    colId: string;
    sort: string;
}

@Component({
    selector: 'ag-export',
    template: require('./ag-export.component.html'),
    styles: [require('./ag-export.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AgExportComponent {

    @Input()
    public gridOptions: GridOptions;

    @Input()
    public showPrint = false;

    @Input()
    public showSave = false;

    public get showSelect() {
        return this.showPrint || this.showSave;
    }

    @Input()
    public showExport = true;

    @Input()
    public exportData: { [key: string]: string }[];

    @Input()
    public exportDisclaimer: string = null;

    @Input()
    public exportGrid: GridOptions = null;

    @Input()
    public showTotal = false;

    @Input()
    public showCount = false;

    @Input()
    public selectedRowNodes: RowNode[];

    @Input()
    public rowData: ExportedData[];

    @Input()
    public checkBoxLabel: string = null;

    @Output()
    public documentsSave = new EventEmitter<any[]>();

    @Output()
    public documentsPrint = new EventEmitter<any[]>();

    @Output()
    public checkBoxClicked = new EventEmitter<boolean>();

    public checkBoxValue = false;

    public exportTypes: ExportType[] = [
        {
            title: 'Export to CSV',
            exporter: () => this.exportCsv()
        },
        {
            title: 'Export to Excel',
            exporter: () => this.exportXlsx()
        }
    ];

    constructor(private router: Router, private dialogService: MdlDialogService) { }

    @Input()
    public handleSort = (sortModels: SortModel[]): SortModel[] => {
        return sortModels;
    }

    public get totalRecords() {
        const api = this.gridOptions.api;
        let totalCount = 0;
        if (api) {
            this.gridOptions.api.forEachNodeAfterFilter((s) => {
                if (s.group) {
                    return;
                }

                totalCount++;
            });
        }
        return totalCount;
    }

    public get unreadCount() {
        const api = this.gridOptions.api;
        let unreadCount = 0;
        if (api) {
            this.gridOptions.api.forEachNodeAfterFilter((s) => {
                if (s.group) {
                    return;
                }

                if (!s.data.isRead) {
                    unreadCount++;
                }
            });
        }
        return unreadCount;
    }

    public getCheckboxIconName(): string {
        if (this.selectedRowNodes.length === 0) {
            return 'check_box_outline_blank';
        }

        const allNodes: RowNode[] = [];

        this.gridOptions.api.forEachNodeAfterFilterAndSort(rowNode => allNodes.push(rowNode));

        if (this.selectedRowNodes.length === allNodes.length) {
            return 'check_box';
        }

        return 'indeterminate_check_box';
    }

    public anyRowsSelected(): boolean {
        return this.selectedRowNodes.length !== 0;
    }

    public anyPrintableRows(): boolean {
        return some(this.rowData, r => r.isPrintable);
    }

    public anyPrintableRowsSelected(): boolean {
        return some(this.selectedRowNodes, (r: RowNode) => r.data && r.data.isPrintable);
    }


    public onClick(exportType: ExportType) {
        exportType.exporter();
    }

    public onSaveDocuments() {
        const data = this.getSelected();
        this.documentsSave.emit(data);
    }

    public async onPrintDocuments() {
        const data = this.getSelected();
        const unprintables = data.filter(r => !r.isPrintable);
        if (!isEmpty(unprintables)) {
            try {
                await this.dialogService
                    .confirm('Some of the selected documents are not printable. Do you wish to continue?', 'No', 'Yes')
                    .pipe(first())
                    .toPromise();
            } catch (ex) {
                return;
            }
            this.documentsPrint.emit(data);
            return;

        } else {
            this.documentsPrint.emit(data);
        }
    }

    public onSaveUnreadDocuments() {
        const data = this.getAllUnread();
        this.documentsSave.emit(data);
    }

    public onPrintUnreadDocuments() {
        const data = this.getAllUnread();
        this.documentsPrint.emit(data);
    }

    public onCheckBoxClicked(event: boolean) {
        this.checkBoxClicked.emit(event);
    }

    public selectUnread() {
        this.gridOptions.api.forEachNodeAfterFilterAndSort((node) => {
            if (node.group) {
                return;
            }
            node.setSelected(!(node.data as ExportedData).isRead);
        });
    }

    public selectAll() {
        this.gridOptions.api.selectAllFiltered();
    }

    public deselectAll() {
        this.gridOptions.api.deselectAll();
    }

    public get isEnabled(): boolean {
        if (!this.rowData) {
            return false;
        }

        return this.rowData.filter(r => !r.isRead && r.isSavable).length !== 0;
    }

    public getUnreadDisabledAttr() {
        return this.isEnabled ? null : 'disabled';
    }

    private getSelected() {
        if (!this.gridOptions.api) {
            return [];
        }
        const data = this.selectedRowNodes.filter(node => !node.group).map(node => node.data);
        return data;
    }

    private getAllUnread(): ExportedData[] {
        if (!this.rowData) {
            return [];
        }

        const result = this.rowData.filter(data => !data.isRead);
        return result;
    }

    private getExportParams(): CsvExportParams {
        const columnsToExport = ((this.exportGrid || this.gridOptions).columnDefs as CovanceGridColumn[])
            .filter(c => !c.noExport && c.field)
            .map(c => c.field);
        const customHeader = this.exportDisclaimer ?
            `"Disclaimer: ${this.exportDisclaimer}"`
            : null;
        return {
            customHeader: customHeader,
            columnKeys: columnsToExport,
            skipGroups: true,
            fileName: this.getExportFileName(),
            processCellCallback: (params: ProcessCellForExportParams) => {
                const renderrer = params.column.getColDef().cellRenderer;
                if (renderrer instanceof Function) {
                    return (renderrer as Function)(params.node);
                }
                return params.value;
            }
        };
    }

    private getExportFileName() {
        const urlTree = this.router.parseUrl(this.router.url);
        urlTree.queryParams = {};
        const urlWithoutQueryParams = this.router.serializeUrl(urlTree);

        const fileName = urlWithoutQueryParams
            .split('/')
            .filter(subPath => !isEmpty(subPath))
            .join('-');

        return fileName;
    }

    private b64toBlob(b64Data: any, contentType: string): Blob {
        const sliceSize = 512;

        const byteCharacters = atob(b64Data);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);

            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);

            byteArrays.push(byteArray);
        }

        const blob = new Blob(byteArrays, { type: contentType });
        return blob;
    }

    private openFileLink(fileName: string, blob: Blob) {
        if (window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(blob, fileName); // IE specific blob download
        } else {
            const elem = document.createElement('a') as HTMLAnchorElement;
            elem.href = URL.createObjectURL(blob);
            elem.download = fileName;
            document.body.appendChild(elem);
            elem.click();
            document.body.removeChild(elem);
        }
    }

    private copyGridSortToExportGrid() {
        if (this.exportGrid) {
            const sortModel = this.handleSort(this.gridOptions.api.getSortModel());
            this.exportGrid.api.setSortModel(sortModel);

            if (this.exportGrid.quickFilterText) {    
                this.exportGrid.api.setQuickFilter(this.exportGrid.quickFilterText);
            }
        }
    }

    // based on https://www.ag-grid.com/javascript-grid-excel/#exportToXlsx
    private exportXlsx() {
        const exportOptions = this.getExportParams();
        this.copyGridSortToExportGrid();
        const csvData = (this.exportGrid || this.gridOptions).api.getDataAsCsv(exportOptions);
        var workBook = xlsx.read(csvData, { type: 'binary', raw: true });

        if (this.exportData != null) {
            var additionalSheet = xlsx.utils.json_to_sheet(this.exportData);
            xlsx.utils.book_append_sheet(workBook, additionalSheet);
        }

        var xlsxContent = xlsx.write(workBook, { bookType: 'xlsx', bookSST: false, type: 'base64' });
        const blob = this.b64toBlob(xlsxContent, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        const fileName = `${exportOptions.fileName}.xlsx`;

        this.openFileLink(fileName, blob);
    }

    private exportCsv() {
        const exportOptions = this.getExportParams();
        this.copyGridSortToExportGrid();
        const csvData = (this.exportGrid || this.gridOptions).api.getDataAsCsv(exportOptions);
        var workBook = xlsx.read(csvData, { type: 'binary', raw: true });

        if (this.exportData != null) { 

            var firstSheetName = workBook.SheetNames ? workBook.SheetNames[0] : null;
            var firstSheet = workBook.Sheets[firstSheetName];
            
            if (firstSheet != null) {
                xlsx.utils.sheet_add_json(
                    firstSheet,
                    this.exportData,
                    { origin: -1 }
                );
            }
        }

        var csvContent = xlsx.write(workBook, { bookType: 'csv', type: 'base64' });
        const blob = this.b64toBlob(csvContent, 'text/csv');
        const fileName = `${exportOptions.fileName}.csv`;

        this.openFileLink(fileName, blob);
    }
}

interface ExportedData {
    isRead: boolean;
    isPrintable: boolean;
    isSavable: boolean;
}
